import { useEffect, useMemo, useState } from 'react'
import { useI18n } from '../i18n'
import { Button, TextField } from '../components/ui'
import { Select } from '../components/ui/Select'
import { useAuth } from '../components/AuthProvider'
import { API_ENDPOINTS } from '../config/api'
import { Edit2, Save, X, HelpCircle } from 'lucide-react'
import { PageLayout } from '../layouts/PageLayout'
import EnterpriseOperationalPage from '../components/enterprise-operational/EnterpriseOperationalPage'
import EnterpriseOperationalSection from '../components/enterprise-operational/EnterpriseOperationalSection'
import entStyles from '../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '@/styles/pages/StarterGuide.module.scss'
import { featureFlags } from '../config/featureFlags'
import { fetchEffectiveStandardAccess } from '../services/standardAccessApi'
import { getActiveOrgId, withOrganizationQuery } from '../utils/orgUtils'
import {
  isStandardAccessPolicyV1Enabled,
  NO_STANDARDS_GRANTED_MESSAGE_KEY,
} from '../utils/standardAccessPolicy'
import { resolveStarterGuideDisplayStandards } from '../utils/starterGuideStandards'
import { normalizeStarterRelevantStandards } from '../utils/standardCanonicalUx'
import { normalizeComplianceStandardCode } from '../utils/complianceStandard'
import {
  starterGuideLocationKey,
  normalizeStarterGuidePhone,
  starterGuideNotSureKey,
  starterGuideResourcesText,
  starterGuideSectorKey,
  starterGuideStrategyKey,
  starterGuideTeamKey,
  starterGuideTestingKey,
} from '../utils/starterGuidePayload'

function unwrapStarterGuideDto(json: unknown): Record<string, unknown> | null {
  if (!json || typeof json !== 'object') return null
  const envelope = json as Record<string, unknown>
  if (envelope.data && typeof envelope.data === 'object') {
    return envelope.data as Record<string, unknown>
  }
  return envelope
}

function mapStarterGuideDto(dto: Record<string, unknown>): StarterData {
  return {
    id: typeof dto.id === 'string' ? dto.id : undefined,
    sector: starterGuideSectorKey(dto.sector),
    hasInformationSystems: !!dto.hasInformationSystems,
    systemLocation: starterGuideLocationKey(dto.systemLocation),
    processesEUCitizenData: !!dto.processesEUCitizenData,
    dataTypes: Array.isArray(dto.dataTypes) ? (dto.dataTypes as string[]) : [],
    hasSecurityStrategy: starterGuideStrategyKey(dto.hasSecurityStrategy),
    hasSecurityResponsible: !!dto.hasSecurityResponsible,
    hasCybersecurityTeam: starterGuideTeamKey(dto.hasCybersecurityTeam),
    hasDisasterRecoveryPlan: starterGuideNotSureKey(dto.hasDisasterRecoveryPlan),
    testsVulnerabilities: starterGuideTestingKey(dto.testsVulnerabilities),
    wantsVulnerabilityReport: !!dto.wantsVulnerabilityReport,
    publicResources: starterGuideResourcesText(dto.publicResources),
    relevantStandards: normalizeStarterRelevantStandards(
      Array.isArray(dto.relevantStandards) ? (dto.relevantStandards as string[]) : [],
    ),
    plansCertifiedAudit: starterGuideNotSureKey(dto.plansCertifiedAudit),
    interestedInPreAudit: !!dto.interestedInPreAudit,
    wantsSelfAssessment: !!dto.wantsSelfAssessment,
    contactName: typeof dto.contactName === 'string' ? dto.contactName : '',
    companyName: typeof dto.companyName === 'string' ? dto.companyName : '',
    email: typeof dto.email === 'string' ? dto.email : '',
    phone: typeof dto.phone === 'string' ? dto.phone : '',
    privacyPolicyAccepted: !!dto.privacyPolicyAccepted,
  }
}

function SummaryRow({
  label,
  value,
  onSave,
  helper,
  enterpriseUx,
}: {
  label: string
  value: string
  onSave: (v: string) => void
  helper?: string
  enterpriseUx?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)

  useEffect(() => {
    setEditValue(value)
  }, [value])

  const handleSave = () => {
    onSave(editValue)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditValue(value)
    setIsEditing(false)
  }

  if (enterpriseUx) {
    return (
      <div className={entStyles.summaryMetric}>
        <div className={styles.summaryLabelRow}>
          <p className={entStyles.summaryLabel}>{label}</p>
          {helper && (
            <span className={styles.tooltipWrap}>
              <HelpCircle className={styles.helpIcon} aria-hidden style={{ color: 'rgba(255,255,255,0.6)' }} />
              <span className={styles.tooltipBubble} role="tooltip">
                {helper}
              </span>
            </span>
          )}
        </div>
        {isEditing ? (
          <div className={styles.summaryEditRow}>
            <div className={styles.summaryEditField}>
              <TextField label={false} value={editValue} onChange={setEditValue} minWidth="100%" aria-label={label} />
            </div>
            <div className={entStyles.formActions}>
              <Button
                type="button"
                size="small"
                color="outline"
                iconOnly
                onClick={handleSave}
                icon={<Save className={styles.iconSm} aria-hidden />}
                aria-label="Save"
              >
                <span className={styles.visuallyHidden}>Save</span>
              </Button>
              <Button
                type="button"
                size="small"
                color="outline"
                iconOnly
                onClick={handleCancel}
                icon={<X className={styles.iconSm} aria-hidden />}
                aria-label="Cancel"
              >
                <span className={styles.visuallyHidden}>Cancel</span>
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.summaryValueRow}>
            <p className={entStyles.summaryValue}>{value}</p>
            <Button
              type="button"
              size="small"
              color="outline"
              iconOnly
              onClick={() => setIsEditing(true)}
              icon={<Edit2 className={styles.iconSm} aria-hidden />}
              aria-label={`Edit ${label}`}
            >
              <span className={styles.visuallyHidden}>Edit</span>
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={styles.summaryRow}>
      <div className={styles.summaryRowMain}>
        <div className={styles.summaryLabelRow}>
          <span className={styles.summaryLabel}>{label}</span>
          {helper && (
            <span className={styles.tooltipWrap}>
              <HelpCircle className={styles.helpIcon} aria-hidden />
              <span className={styles.tooltipBubble} role="tooltip">
                {helper}
              </span>
            </span>
          )}
        </div>
        {isEditing ? (
          <div className={styles.summaryEditRow}>
            <div className={styles.summaryEditField}>
              <TextField label={false} value={editValue} onChange={setEditValue} minWidth="100%" aria-label={label} />
            </div>
            <Button
              type="button"
              size="small"
              color="primary"
              iconOnly
              onClick={handleSave}
              icon={<Save className={styles.iconSm} aria-hidden />}
              aria-label="Save"
            >
              <span className={styles.visuallyHidden}>Save</span>
            </Button>
            <Button
              type="button"
              size="small"
              color="outline"
              iconOnly
              onClick={handleCancel}
              icon={<X className={styles.iconSm} aria-hidden />}
              aria-label="Cancel"
            >
              <span className={styles.visuallyHidden}>Cancel</span>
            </Button>
          </div>
        ) : (
          <div className={styles.summaryValueRow}>
            <span className={styles.summaryValue}>{value}</span>
            <Button
              type="button"
              size="small"
              color="outline"
              iconOnly
              onClick={() => setIsEditing(true)}
              icon={<Edit2 className={styles.iconSm} aria-hidden />}
              aria-label={`Edit ${label}`}
            >
              <span className={styles.visuallyHidden}>Edit</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

interface StarterData {
  id?: string
  sector: string
  hasInformationSystems: boolean
  systemLocation?: string
  processesEUCitizenData: boolean
  dataTypes: string[]
  hasSecurityStrategy: string
  hasSecurityResponsible: boolean
  hasCybersecurityTeam: string
  hasDisasterRecoveryPlan: string
  testsVulnerabilities: string
  wantsVulnerabilityReport: boolean
  publicResources?: string
  relevantStandards: string[]
  plansCertifiedAudit: string
  interestedInPreAudit: boolean
  wantsSelfAssessment: boolean
  contactName: string
  companyName: string
  email: string
  phone: string
  privacyPolicyAccepted: boolean
}

const SECTORS_KEYS = ['banking', 'chemical', 'digitalInfrastructure', 'digitalServiceProviders', 'healthcare', 'transport', 'other']
const LOCATIONS_KEYS = ['onPremise', 'rented', 'cloud', 'notSure']
const DATATYPES_KEYS = ['name', 'email', 'phone', 'financial', 'address', 'other']

function emptyStarterGuide(): StarterData {
  return {
    sector: '',
    hasInformationSystems: false,
    systemLocation: '',
    processesEUCitizenData: false,
    dataTypes: [],
    hasSecurityStrategy: 'no',
    hasSecurityResponsible: false,
    hasCybersecurityTeam: 'no',
    hasDisasterRecoveryPlan: 'notSure',
    testsVulnerabilities: 'no',
    wantsVulnerabilityReport: false,
    publicResources: '',
    relevantStandards: [],
    plansCertifiedAudit: 'notSure',
    interestedInPreAudit: false,
    wantsSelfAssessment: false,
    contactName: '',
    companyName: '',
    email: '',
    phone: '',
    privacyPolicyAccepted: false,
  }
}

function toStarterGuideWriteBody(data: StarterData) {
  return {
    sector: data.sector,
    hasInformationSystems: data.hasInformationSystems,
    systemLocation: data.systemLocation || '',
    processesEUCitizenData: data.processesEUCitizenData,
    dataTypes: data.dataTypes,
    hasSecurityStrategy: data.hasSecurityStrategy,
    hasSecurityResponsible: data.hasSecurityResponsible,
    hasCybersecurityTeam: data.hasCybersecurityTeam,
    hasDisasterRecoveryPlan: data.hasDisasterRecoveryPlan,
    testsVulnerabilities: data.testsVulnerabilities,
    wantsVulnerabilityReport: data.wantsVulnerabilityReport,
    publicResources: data.publicResources || '',
    relevantStandards: data.relevantStandards,
    plansCertifiedAudit: data.plansCertifiedAudit,
    interestedInPreAudit: data.interestedInPreAudit,
    wantsSelfAssessment: data.wantsSelfAssessment,
    contactName: data.contactName,
    companyName: data.companyName,
    email: data.email,
    phone: data.phone,
    privacyPolicyAccepted: data.privacyPolicyAccepted === true,
  }
}

function starterFieldErrors(json: unknown, t: (key: string, fallback?: string) => string) {
  const envelope = json && typeof json === 'object' ? (json as Record<string, unknown>) : {}
  const fields = Array.isArray(envelope.fields) ? envelope.fields : []
  const errors: Record<string, string> = {}
  for (const field of fields) {
    const row = field as { field?: string; code?: string }
    if (!row.field || errors[row.field]) continue
    errors[row.field] = t(`starter.validation.${row.code || 'invalid'}`, row.code || 'invalid')
  }
  return errors
}

function FieldAlert({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p role="alert" style={{ margin: '0.25rem 0 0', color: '#b42318', fontSize: '0.875rem' }}>
      {message}
    </p>
  )
}

export default function StarterGuide() {
  const { t } = useI18n()
  const enterpriseUx = featureFlags.ENTERPRISE_OPERATIONAL_UX_V1

  const getDataTypes = () => DATATYPES_KEYS.map((key) => t(`starterGuide.dataTypes.${key}`))
  const sectorOptions = SECTORS_KEYS.map((key) => ({ value: key, label: t(`starterGuide.sectors.${key}`) }))
  const locationOptions = LOCATIONS_KEYS.map((key) => ({ value: key, label: t(`starterGuide.locations.${key}`) }))
  const yesNoOptions = [
    { value: 'yes', label: t('starterGuide.options.yes') },
    { value: 'no', label: t('starterGuide.options.no') },
  ]
  const strategyOptions = [...yesNoOptions, { value: 'inDevelopment', label: t('starterGuide.options.inDevelopment') }]
  const teamOptions = [...yesNoOptions, { value: 'externalProvider', label: t('starterGuide.options.externalProvider') }]
  const notSureOptions = [...yesNoOptions, { value: 'notSure', label: t('starterGuide.options.notSure') }]
  const testingOptions = [...yesNoOptions, { value: 'sometimes', label: t('starterGuide.options.sometimes') }]

  const { token } = useAuth()
  const [orgId, setOrgId] = useState<string | null>(() => getActiveOrgId())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accessDenied, setAccessDenied] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [data, setData] = useState<StarterData | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [savedHint, setSavedHint] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [grantedCatalog, setGrantedCatalog] = useState<string[] | null>(null)
  const [policyNotice, setPolicyNotice] = useState<string | null>(null)

  useEffect(() => {
    const syncOrg = () => setOrgId(getActiveOrgId())
    window.addEventListener('ds-active-organization-changed', syncOrg)
    return () => window.removeEventListener('ds-active-organization-changed', syncOrg)
  }, [])

  const displayStandards = useMemo(() => {
    if (grantedCatalog === null) {
      return resolveStarterGuideDisplayStandards(null)
    }
    if (isStandardAccessPolicyV1Enabled() && grantedCatalog.length === 0) {
      return []
    }
    return resolveStarterGuideDisplayStandards(grantedCatalog)
  }, [grantedCatalog])

  useEffect(() => {
    if (!token || !orgId) return
    void fetchEffectiveStandardAccess(orgId)
      .then((effective) => {
        const codes = effective.standardCodes || []
        setGrantedCatalog(codes)
        if (isStandardAccessPolicyV1Enabled() && codes.length === 0) {
          setPolicyNotice(NO_STANDARDS_GRANTED_MESSAGE_KEY)
        } else {
          setPolicyNotice(null)
        }
      })
      .catch(() => setGrantedCatalog(null))
  }, [token, orgId])

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError('')
        setAccessDenied(false)
        // Clear previous org questionnaire before fetch (no cross-org bleed)
        setData(null)

        const url = withOrganizationQuery(API_ENDPOINTS.STARTER_GUIDE, orgId)
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        })

        if (res.status === 403) {
          setAccessDenied(true)
          setData(null)
          setError(
            t('starter.accessDenied', 'You do not have permission to access the Starter Guide for this organization.'),
          )
          return
        }

        if (res.status === 404) {
          const body = await res.json().catch(() => ({} as { code?: string }))
          // Contract 404: no questionnaire yet — show empty form. Other 404s = route/proxy miss.
          if (body?.code === 'QUESTIONNAIRE_NOT_FOUND') {
            setData(emptyStarterGuide())
            setEditMode(true)
            return
          }
          setError(
            t(
              'starter.routeMissing',
              'Starter guide API is unavailable (route not found). Check backend /api/starter-guide mount.',
            ),
          )
          setData(null)
          return
        }
        if (!res.ok) {
          setError(t('starter.loadFailed', 'Failed to load starter guide'))
          setData(null)
          return
        }
        const payload = unwrapStarterGuideDto(await res.json())
        if (payload) {
          setData(mapStarterGuideDto(payload))
          setEditMode(false)
        } else {
          setData(emptyStarterGuide())
          setEditMode(true)
        }
      } catch (e: any) {
        console.error('StarterGuide load error:', e)
        setError(e.message || 'Failed to load starter guide data')
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      void load()
    } else {
      setLoading(false)
    }
  }, [token, t, orgId])

  useEffect(() => {
    if (!data) return
    try {
      localStorage.setItem(
        'starter_guide_flags',
        JSON.stringify({
          relevantStandards: data.relevantStandards || [],
          processesEUCitizenData: !!data.processesEUCitizenData,
        }),
      )
    } catch {}
  }, [data?.relevantStandards, data?.processesEUCitizenData])

  const validate = (_d: StarterData) => ''

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!data) return

    validate(data)
    const localErrors: Record<string, string> = {}
    if (!data.sector.trim()) localErrors.sector = t('starter.validation.required', 'Required')
    if (!data.contactName.trim()) localErrors.contactName = t('starter.validation.required', 'Required')
    if (!data.companyName.trim()) localErrors.companyName = t('starter.validation.required', 'Required')
    if (!data.email.trim()) localErrors.email = t('starter.validation.required', 'Required')
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) localErrors.email = t('starter.validation.invalid_format', 'Invalid format')
    if (!data.phone.trim()) localErrors.phone = t('starter.validation.required', 'Required')
    else if (!normalizeStarterGuidePhone(data.phone)) localErrors.phone = t('starter.validation.invalid_format', 'Invalid format')
    if (data.privacyPolicyAccepted !== true) localErrors.privacyPolicyAccepted = t('starter.validation.required', 'Required')
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors)
      setError('')
      setSavedHint('')
      return
    }

    setSubmitting(true)
    setError('')
    setFieldErrors({})

    try {
      const body = toStarterGuideWriteBody(data)
      const writeUrl = data.id
        ? withOrganizationQuery(`${API_ENDPOINTS.STARTER_GUIDE}/${data.id}`, orgId)
        : withOrganizationQuery(API_ENDPOINTS.STARTER_GUIDE, orgId)
      const res = await fetch(writeUrl, {
        method: data.id ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      const json = await res.json().catch(() => null)
      if (!res.ok) {
        const errors = starterFieldErrors(json, t)
        setFieldErrors(errors)
        setError(Object.keys(errors).length ? '' : t('starter.validation.failed', 'Validation failed'))
        setSavedHint('')
        return
      }
      const payload = unwrapStarterGuideDto(json)
      if (payload) setData(mapStarterGuideDto(payload))

      try {
        localStorage.setItem(
          'starter_guide_flags',
          JSON.stringify({
            relevantStandards: data.relevantStandards || [],
            processesEUCitizenData: !!data.processesEUCitizenData,
          }),
        )
      } catch {}

      setSavedHint(`Saved ${new Date().toLocaleString()}`)
      setEditMode(false)
    } catch {
      setError(t('starter.validation.failed', 'Validation failed'))
    } finally {
      setSubmitting(false)
    }
  }

  const exportData = async (format: 'pdf' | 'excel') => {
    if (!data?.id) return
    const res = await fetch(`${API_ENDPOINTS.STARTER_GUIDE}/${data.id}/export?format=${format}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) return
    const payload = await res.json()
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `starter-guide.${format}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  async function saveInline<K extends keyof StarterData>(key: K, value: StarterData[K]) {
    if (!data?.id) return
    const updated = { ...data, [key]: value }
    setData(updated)
    try {
      await fetch(`${API_ENDPOINTS.STARTER_GUIDE}/${data.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      })
    } catch {}
  }

  async function saveRelevantStandards(standards: string[]) {
    const previous = data?.relevantStandards || []
    const updated = { ...(data || {}), relevantStandards: standards } as StarterData
    setData(updated)
    try {
      const res = await fetch(API_ENDPOINTS.STARTER_GUIDE_RELEVANT_STANDARDS, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ relevantStandards: standards }),
      })
      const json = await res.json().catch(() => null)
      const payload = unwrapStarterGuideDto(json)
      if (!res.ok) {
        if (data) setData({ ...data, relevantStandards: previous })
        const message =
          (typeof payload?.message === 'string' && payload.message) ||
          (typeof (payload as { error?: string } | null)?.error === 'string' &&
            (payload as { error?: string }).error) ||
          t('starter.standardsSaveFailed', 'Failed to save standards')
        setError(message)
        return
      }
      const saved = normalizeStarterRelevantStandards(
        Array.isArray(payload?.relevantStandards)
          ? (payload.relevantStandards as string[])
          : Array.isArray((payload as { data?: { relevantStandards?: string[] } })?.data?.relevantStandards)
            ? ((payload as { data: { relevantStandards: string[] } }).data.relevantStandards)
            : standards,
      )
      const responseDto =
        payload && typeof (payload as { data?: unknown }).data === 'object'
          ? unwrapStarterGuideDto((payload as { data: unknown }).data)
          : payload
      const savedId = typeof payload?.id === 'string' ? payload.id : typeof responseDto?.id === 'string' ? responseDto.id : data?.id
      setData((prev) =>
        prev
          ? { ...prev, ...(savedId ? { id: savedId } : {}), relevantStandards: saved }
          : ({ relevantStandards: saved, ...(savedId ? { id: savedId } : {}) } as StarterData),
      )
      setError('')
    } catch {
      if (data) setData({ ...data, relevantStandards: previous })
      setError(t('starter.standardsSaveFailed', 'Failed to save standards'))
    }
  }

  function isStandardChecked(opt: string, selected: string[] | undefined) {
    const canon = normalizeComplianceStandardCode(opt)
    return (selected || []).some((s) => normalizeComplianceStandardCode(s) === canon)
  }

  function toggleStandard(opt: string) {
    if (!data) return
    if (!displayStandards.includes(opt)) return
    const selected = (data.relevantStandards || []).filter((s) =>
      displayStandards.some((d) => normalizeComplianceStandardCode(d) === normalizeComplianceStandardCode(s)),
    )
    const set = new Set(selected)
    const canon = normalizeComplianceStandardCode(opt)
    const existing = selected.find((s) => normalizeComplianceStandardCode(s) === canon)
    if (existing) set.delete(existing)
    else set.add(opt)
    void saveRelevantStandards(Array.from(set))
  }

  if (loading) {
    return (
      <PageLayout pageScope="starter-guide-page" title={t('nav.starterGuide', 'Starter Guide')} contentClassName={styles.pageContent}>
        {enterpriseUx ? (
          <EnterpriseOperationalPage>
            <div className={entStyles.loadingRow}>
              <div className={styles.spinner} aria-hidden />
              <span>{t('starter.loading')}</span>
            </div>
          </EnterpriseOperationalPage>
        ) : (
          <div className={styles.loadingRow}>
            <div className={styles.spinner} aria-hidden />
            <span className={styles.loadingText}>{t('starter.loading')}</span>
          </div>
        )}
      </PageLayout>
    )
  }

  if (accessDenied) {
    return (
      <PageLayout pageScope="starter-guide-page" title={t('nav.starterGuide', 'Starter Guide')} contentClassName={styles.pageContent}>
        <p className={enterpriseUx ? entStyles.policyNotice : styles.policyNotice} role="alert">
          {error || t('starter.accessDenied', 'You do not have permission to access the Starter Guide for this organization.')}
        </p>
      </PageLayout>
    )
  }

  const summaryBody = data?.id && !editMode && (
    <>
      <div className={enterpriseUx ? entStyles.summaryGrid : styles.summaryGrid}>
        <SummaryRow enterpriseUx={enterpriseUx} label={t('starter.summary.company')} value={data?.companyName || '-'} onSave={(v) => saveInline('companyName', v)} />
        <SummaryRow enterpriseUx={enterpriseUx} label={t('starter.summary.user')} value={data?.contactName || '-'} onSave={(v) => saveInline('contactName', v)} />
        <SummaryRow enterpriseUx={enterpriseUx} label={t('starter.summary.email')} value={data?.email || '-'} onSave={(v) => saveInline('email', v)} />
        <SummaryRow enterpriseUx={enterpriseUx} label={t('starter.summary.phone')} value={data?.phone || '-'} onSave={(v) => saveInline('phone', v)} />
        <SummaryRow
          enterpriseUx={enterpriseUx}
          label={t('starter.summary.regSystems')}
          value={(data?.publicResources || '').trim() || '-'}
          onSave={(v) => saveInline('publicResources', v)}
          helper={t('starter.summary.regSystemsHelper')}
        />
      </div>
      <div className={enterpriseUx ? entStyles.recommendedBlock : styles.recommendedBlock}>
        <p className={enterpriseUx ? entStyles.postureMetricLabel : styles.recommendedTitle}>
          {t('starter.summary.availableStandards', 'Available standards')}
        </p>
        {(data?.relevantStandards || []).filter((s) => displayStandards.includes(s)).length > 0 ? (
          <div className={enterpriseUx ? entStyles.chipRow : styles.chipRow}>
            {(data?.relevantStandards || [])
              .filter((s) => displayStandards.includes(s))
              .map((s) => (
                <span key={s} className={enterpriseUx ? entStyles.standardChip : styles.standardChip}>
                  {s}
                </span>
              ))}
          </div>
        ) : null}
        {policyNotice && isStandardAccessPolicyV1Enabled() ? (
          <p className={enterpriseUx ? entStyles.policyNotice : styles.policyNotice} role="status">
            {t(policyNotice, 'No standards granted for this organization or user.')}
          </p>
        ) : null}
        {isStandardAccessPolicyV1Enabled() ? (
          <p className={enterpriseUx ? entStyles.policyNoticeMuted : styles.policyNoticeMuted}>
            {t('standardGovernance.policyBanner')}
          </p>
        ) : null}
        <div className={enterpriseUx ? entStyles.checkboxGrid : styles.checkboxGrid}>
          {displayStandards.map((opt) => (
            <label key={opt} className={enterpriseUx ? entStyles.checkboxLabel : styles.checkboxLabel}>
              <input type="checkbox" checked={isStandardChecked(opt, data?.relevantStandards)} onChange={() => toggleStandard(opt)} />
              {opt}
            </label>
          ))}
        </div>
      </div>
      <div className={enterpriseUx ? entStyles.formActions : styles.summaryActions}>
        <Button color={enterpriseUx ? 'outline' : 'secondary'} size={enterpriseUx ? 'small' : undefined} onClick={() => setEditMode(true)}>
          {t('starter.btn.editForm')}
        </Button>
        <div className={styles.exportGroup}>
          <Button color={enterpriseUx ? 'outline' : 'secondary'} size={enterpriseUx ? 'small' : undefined} onClick={() => void exportData('pdf')}>
            {t('starter.btn.exportPdf')}
          </Button>
          <Button color={enterpriseUx ? 'outline' : 'primary'} size={enterpriseUx ? 'small' : undefined} onClick={() => void exportData('excel')}>
            {t('starter.btn.exportExcel')}
          </Button>
        </div>
      </div>
    </>
  )

  const editForm = (
              <form className={enterpriseUx ? entStyles.formStack : styles.formStack} onSubmit={handleSubmit}>
                <SelectField
                  label={t('starter.form.sector')}
                  value={data?.sector || ''}
                  onChange={(v) => {
                    setFieldErrors((prev) => ({ ...prev, sector: '' }))
                    setData((prev) => ({ ...(prev || ({} as StarterData)), sector: v }))
                  }}
                  options={[{ value: '', label: '' }, ...sectorOptions]}
                />
                <FieldAlert message={fieldErrors.sector} />
                <ToggleYN
                  label={t('starter.form.hasIS')}
                  value={!!data?.hasInformationSystems}
                  onChange={(v) =>
                    setData((prev) => ({
                      ...(prev || ({} as StarterData)),
                      hasInformationSystems: v,
                      systemLocation: v ? prev?.systemLocation || '' : '',
                    }))
                  }
                />
                {data?.hasInformationSystems && (
                  <SelectField
                    label={t('starter.form.location')}
                    value={data?.systemLocation || ''}
                    onChange={(v) => setData((prev) => ({ ...(prev as StarterData), systemLocation: v }))}
                    options={[{ value: '', label: '' }, ...locationOptions]}
                  />
                )}
                <ToggleYN
                  label={t('starter.form.processEU')}
                  value={!!data?.processesEUCitizenData}
                  onChange={(v) =>
                    setData((prev) => ({
                      ...(prev || ({} as StarterData)),
                      processesEUCitizenData: v,
                      dataTypes: v ? prev?.dataTypes || [] : [],
                    }))
                  }
                />
                {data?.processesEUCitizenData && (
                  <CheckboxGroup
                    label={t('starter.form.dataTypes')}
                    options={getDataTypes()}
                    values={data?.dataTypes || []}
                    onChange={(vals) => setData((prev) => ({ ...(prev as StarterData), dataTypes: vals }))}
                  />
                )}
                <SelectField
                  label={t('starter.form.strategy')}
                  value={data?.hasSecurityStrategy || 'no'}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), hasSecurityStrategy: v }))}
                  options={strategyOptions}
                />
                <ToggleYN
                  label={t('starter.form.responsible')}
                  value={!!data?.hasSecurityResponsible}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), hasSecurityResponsible: v }))}
                />
                <SelectField
                  label={t('starter.form.team')}
                  value={data?.hasCybersecurityTeam || 'no'}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), hasCybersecurityTeam: v }))}
                  options={teamOptions}
                />
                <SelectField
                  label={t('starter.form.drp')}
                  value={data?.hasDisasterRecoveryPlan || 'notSure'}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), hasDisasterRecoveryPlan: v }))}
                  options={notSureOptions}
                />
                <SelectField
                  label={t('starter.form.vulnTest')}
                  value={data?.testsVulnerabilities || 'no'}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), testsVulnerabilities: v }))}
                  options={testingOptions}
                />
                <ToggleYN
                  label={t('starter.form.wantReport')}
                  value={!!data?.wantsVulnerabilityReport}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), wantsVulnerabilityReport: v }))}
                />
                {data?.wantsVulnerabilityReport && (
                  <TextField
                    label={t('starter.form.publicResources')}
                    multiline
                    rows={4}
                    value={data?.publicResources || ''}
                    onChange={(v) => setData((prev) => ({ ...(prev as StarterData), publicResources: v }))}
                    minWidth="100%"
                  />
                )}
                <CheckboxGroup
                  label={t('starter.form.standards')}
                  options={displayStandards}
                  values={data?.relevantStandards || []}
                  onChange={(vals) => {
                    const next = { ...(data as StarterData), relevantStandards: vals }
                    setData(next)
                    try {
                      localStorage.setItem(
                        'starter_guide_flags',
                        JSON.stringify({
                          relevantStandards: vals,
                          processesEUCitizenData: !!next.processesEUCitizenData,
                        }),
                      )
                    } catch {}
                  }}
                />
                <SelectField
                  label={t('starter.form.certAudit')}
                  value={data?.plansCertifiedAudit || 'notSure'}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), plansCertifiedAudit: v }))}
                  options={notSureOptions}
                />
                <ToggleYN
                  label={t('starter.form.preAudit')}
                  value={!!data?.interestedInPreAudit}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), interestedInPreAudit: v }))}
                />
                <ToggleYN
                  label={t('starter.form.selfAssess')}
                  value={!!data?.wantsSelfAssessment}
                  onChange={(v) => setData((prev) => ({ ...(prev || ({} as StarterData)), wantsSelfAssessment: v }))}
                />
                <TextField
                  label={t('starter.form.name')}
                  value={data?.contactName || ''}
                  required
                  invalid={!!fieldErrors.contactName}
                  onChange={(v) => {
                    setFieldErrors((prev) => ({ ...prev, contactName: '' }))
                    setData((prev) => ({ ...(prev || ({} as StarterData)), contactName: v }))
                  }}
                  minWidth="100%"
                  autoComplete="name"
                />
                <FieldAlert message={fieldErrors.contactName} />
                <TextField
                  label={t('starter.form.companyName')}
                  value={data?.companyName || ''}
                  required
                  invalid={!!fieldErrors.companyName}
                  onChange={(v) => {
                    setFieldErrors((prev) => ({ ...prev, companyName: '' }))
                    setData((prev) => ({ ...(prev || ({} as StarterData)), companyName: v }))
                  }}
                  minWidth="100%"
                />
                <FieldAlert message={fieldErrors.companyName} />
                <TextField
                  label={t('starter.form.email')}
                  type="email"
                  value={data?.email || ''}
                  required
                  invalid={!!fieldErrors.email}
                  onChange={(v) => {
                    setFieldErrors((prev) => ({ ...prev, email: '' }))
                    setData((prev) => ({ ...(prev || ({} as StarterData)), email: v }))
                  }}
                  minWidth="100%"
                  autoComplete="email"
                />
                <FieldAlert message={fieldErrors.email} />
                <TextField
                  label={t('starter.form.phone')}
                  value={data?.phone || ''}
                  required
                  invalid={!!fieldErrors.phone}
                  onChange={(v) => {
                    setFieldErrors((prev) => ({ ...prev, phone: '' }))
                    setData((prev) => ({ ...(prev || ({} as StarterData)), phone: v }))
                  }}
                  minWidth="100%"
                  autoComplete="tel"
                />
                <FieldAlert message={fieldErrors.phone} />
                <label className={enterpriseUx ? entStyles.checkboxRow : styles.privacyRow}>
                  <input
                    type="checkbox"
                    checked={!!data?.privacyPolicyAccepted}
                    onChange={(e) => {
                      setFieldErrors((prev) => ({ ...prev, privacyPolicyAccepted: '' }))
                      setData((prev) => ({ ...(prev || ({} as StarterData)), privacyPolicyAccepted: e.target.checked }))
                    }}
                  />
                  {t('starter.form.privacy')}
                </label>
                <FieldAlert message={fieldErrors.privacyPolicyAccepted} />
                <div className={enterpriseUx ? entStyles.formActions : styles.submitRow}>
                  <Button type="submit" color={enterpriseUx ? 'outline' : 'primary'} size={enterpriseUx ? 'small' : undefined} disabled={submitting}>
                    {submitting ? t('starter.btn.saving') : t('starter.btn.save')}
                  </Button>
                  {data && editMode && (
                    <Button type="button" color={enterpriseUx ? 'outline' : 'secondary'} size={enterpriseUx ? 'small' : undefined} onClick={() => setEditMode(false)}>
                      {t('starter.btn.cancel')}
                    </Button>
                  )}
                </div>
              </form>
  )

  const starterContent = (
    <>
      {error && (
        <p className={enterpriseUx ? entStyles.policyNotice : styles.policyNotice} role="alert">
          {error}
        </p>
      )}
      {savedHint && (
        <p className={enterpriseUx ? entStyles.savedHint : styles.savedHint}>
          {t('common.saved')} {savedHint.split(' ').slice(1).join(' ')}
        </p>
      )}

      {summaryBody &&
        (enterpriseUx ? (
          <EnterpriseOperationalSection
            layerLabel={t('starter.layerLabel', 'Organization onboarding')}
            title={t('starter.summary.title')}
            subtitle={t('starter.header.p1')}
            testId="starter-summary-enterprise"
          >
            {summaryBody}
          </EnterpriseOperationalSection>
        ) : (
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle}>{t('starter.summary.title')}</h2>
            </div>
            <div className={styles.panelBody}>{summaryBody}</div>
          </div>
        ))}

      {(!data?.id || editMode) &&
        (enterpriseUx ? (
          <EnterpriseOperationalSection
            layerLabel={t('starter.formLayerLabel', 'Security posture questionnaire')}
            title={t('starter.header.title')}
            subtitle={
              <>
                <span>{t('starter.header.p1')}</span>
                <br />
                <span>{t('starter.header.p2')}</span>
              </>
            }
            testId="starter-form-enterprise"
          >
            {editForm}
          </EnterpriseOperationalSection>
        ) : (
          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle}>{t('starter.header.title')}</h2>
              <div className={styles.panelIntro}>
                <p className={styles.panelIntroText}>{t('starter.header.p1')}</p>
                <p className={styles.panelIntroText}>{t('starter.header.p2')}</p>
              </div>
            </div>
            <div className={styles.panelBody}>{editForm}</div>
          </div>
        ))}
    </>
  )

  return (
    <PageLayout
      pageScope="starter-guide-page"
      title={t('nav.starterGuide', 'Starter Guide')}
      description={t('starter.header.p1')}
      contentClassName={styles.pageContent}
    >
      <div className={styles.inner}>
        {enterpriseUx ? <EnterpriseOperationalPage>{starterContent}</EnterpriseOperationalPage> : starterContent}
      </div>
    </PageLayout>
  )
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: Array<string | { value: string; label: string }>
}) {
  const { t } = useI18n()
  const enterpriseUx = featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
  const normalized = options.map((option) =>
    typeof option === 'string' ? { value: option, label: option || t('common.select') } : { value: option.value, label: option.label || t('common.select') },
  )
  return (
    <Select<string>
      label={label}
      minWidth="100%"
      variant={enterpriseUx ? 'outline' : 'default'}
      options={normalized}
      value={value}
      onChange={(v) => v != null && onChange(v)}
      placeholder={t('common.select')}
    />
  )
}

function ToggleYN({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  const { t } = useI18n()
  const enterpriseUx = featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
  return (
    <div>
      <span className={enterpriseUx ? entStyles.postureMetricLabel : styles.fieldLabel}>{label}</span>
      <div className={styles.radioRow}>
        <label className={enterpriseUx ? entStyles.checkboxLabel : styles.radioLabel}>
          <input type="radio" checked={value} onChange={() => onChange(true)} />
          {t('common.yes')}
        </label>
        <label className={enterpriseUx ? entStyles.checkboxLabel : styles.radioLabel}>
          <input type="radio" checked={!value} onChange={() => onChange(false)} />
          {t('common.no')}
        </label>
      </div>
    </div>
  )
}

function CheckboxGroup({
  label,
  options,
  values,
  onChange,
}: {
  label: string
  options: string[]
  values: string[]
  onChange: (vals: string[]) => void
}) {
  const enterpriseUx = featureFlags.ENTERPRISE_OPERATIONAL_UX_V1
  return (
    <div>
      <span className={enterpriseUx ? entStyles.postureMetricLabel : styles.fieldLabel}>{label}</span>
      <div className={enterpriseUx ? entStyles.checkboxGrid : styles.checkboxGrid}>
        {options.map((opt) => (
          <label key={opt} className={enterpriseUx ? entStyles.checkboxLabel : styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={values.includes(opt)}
              onChange={() => {
                const set = new Set(values)
                if (set.has(opt)) set.delete(opt)
                else set.add(opt)
                onChange(Array.from(set))
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  )
}
